import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';
import { store } from "../../../store";
import { baseUrl } from '../../../Constant/Url';

const UploadContext = createContext();

export const useUpload = () => useContext(UploadContext);

export const UploadProvider = ({ children }) => {
    const [uploads, setUploads] = useState([]);
    const [isWidgetOpen, setIsWidgetOpen] = useState(false);

    const updateUploadItem = (id, updates) => {
        setUploads(prev => prev.map(item => item.id === id ? { ...item, ...updates } : item));
    };

    const removeUpload = (id) => {
        setUploads((prev) => prev.filter((item) => item.id !== id));
    };

    const getVideoMetadata = (file) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                const metadata = {
                    duration: video.duration,
                    height: video.videoHeight
                };
                window.URL.revokeObjectURL(video.src);
                resolve(metadata);
            };
            video.onerror = (e) => {
                window.URL.revokeObjectURL(video.src);
                reject(e);
            };
            video.src = URL.createObjectURL(file);
        });
    };

    // ---UPDATE ANSWER---
    const handleUpdateAnswerFeatured = async (apiData, recordId, token) => {
        try {
            // Payload Structure 
            const payload = {
                question_id: recordId, // "id" -> "question_id"
                media_url: apiData.videoUrl || apiData.media_url,
                thumbnail_url: apiData.thumbnailUrl || apiData.thumbnail_url,
                thumbnail_cropped: apiData.thumbnailCropUrl || apiData.thumbnail_cropped,
                jobId: apiData.transcribeJobId || apiData.jobId,
                duration: apiData.duration,
                is_revert_original: true
            };

            const response = await axios.put(`${baseUrl}admin/user/update_answer`, payload, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            console.error("Update Answer Failed", error);
            throw error;
        }
    };

    const startBackgroundUpload = async (file, record ,onSuccess) => {
        if (!file) return;

        const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mov'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only video files allowed.');
            return;
        }

        const uploadId = Date.now().toString();

        const newUpload = {
            id: uploadId,
            recordId: record._id,
            fileName: file.name,
            progress: 0,
            status: 'Preparing...',
            state: 'uploading'
        };

        setUploads(prev => [...prev, newUpload]);
        setIsWidgetOpen(true);

        try {
            const tukanUpAdminToken = store.getState()?.common?.admin_token;
            const { duration, height } = await getVideoMetadata(file);

            // 20MB Chunks
            const CHUNK_SIZE = 20 * 1024 * 1024;
            const totalParts = Math.ceil(file.size / CHUNK_SIZE);

            updateUploadItem(uploadId, { status: `Initiating (${totalParts} parts)...` });

            // 1. Init
            const initRes = await axios.post(`${baseUrl}/user/auth/upload/multipart/init`, {
                fileName: file.name,
                fileType: file.type
            }, { headers: { 'Authorization': `Bearer ${tukanUpAdminToken}` } });

            const { uploadId: awsUploadId, key } = initRes.data.data;
            let completedPartsCount = 0;

            // 2. Upload Parts
            const uploadPart = async (partNumber) => {
                const start = (partNumber - 1) * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                const signRes = await axios.post(`${baseUrl}/user/auth/upload/multipart/sign-part`, {
                    key, uploadId: awsUploadId, partNumber
                }, { headers: { 'Authorization': `Bearer ${tukanUpAdminToken}` } });

                const s3Res = await axios.put(signRes.data.data.url, chunk, {
                    headers: { 'Content-Type': file.type }
                });

                completedPartsCount++;
                const percent = Math.round((completedPartsCount / totalParts) * 100);
                updateUploadItem(uploadId, { progress: percent, status: `Uploading... ${percent}%` });

                return { PartNumber: partNumber, ETag: s3Res.headers['etag'].replaceAll('"', '') };
            };

            const CONCURRENCY_LIMIT = 4;
            const partsToUpload = Array.from({ length: totalParts }, (_, i) => i + 1);
            const executing = [];
            const results = [];

            for (const partNumber of partsToUpload) {
                const p = uploadPart(partNumber).then(res => {
                    executing.splice(executing.indexOf(p), 1);
                    return res;
                });
                results.push(p);
                executing.push(p);
                if (executing.length >= CONCURRENCY_LIMIT) await Promise.race(executing);
            }

            const sortedParts = (await Promise.all(results)).sort((a, b) => a.PartNumber - b.PartNumber);

            // 3. Complete
            await axios.post(`${baseUrl}/user/auth/upload/multipart/complete`, {
                key, uploadId: awsUploadId, parts: sortedParts
            }, { headers: { 'Authorization': `Bearer ${tukanUpAdminToken}` } });

            // 4. Process Admin
            updateUploadItem(uploadId, { status: "Processing video...", state: 'processing' });

            const processRes = await axios.post(`${baseUrl}/user/auth/upload/process_file_admin`,
                { s3_key: key, original_name: file.name, duration: duration, input_height: height },
                { headers: { 'Authorization': `Bearer ${tukanUpAdminToken}` } }
            );

            // 5. Update DB
            if (processRes.status === 200 || processRes.data?.status) {
                updateUploadItem(uploadId, { progress: 100, status: "Updating Database..." });

                await handleUpdateAnswerFeatured(processRes.data.data, record._id, tukanUpAdminToken);

                updateUploadItem(uploadId, { status: "Completed", state: 'success' });

                // --- CALL THE REFRESH FUNCTION ---
                if (onSuccess && typeof onSuccess === 'function') {
                    console.log("Upload complete, refreshing list...");
                    onSuccess(); // <--- This triggers fetchQueueData() in UsersDetails
                }
                // -----------------------------------------------

                setTimeout(() => {
                    removeUpload(uploadId);
                }, 3000);

            } else {
                throw new Error("Processing failed.");
            }

        } catch (error) {
            console.error("Upload Error:", error);
            updateUploadItem(uploadId, { status: "Failed", state: 'error' });
        }
    };

    return (
        <UploadContext.Provider value={{ uploads, startBackgroundUpload, isWidgetOpen, setIsWidgetOpen, removeUpload }}>
            {children}
        </UploadContext.Provider>
    );
};